import { ThemeOptions, createTheme } from '@mui/material/styles'

import primitive from './token/primitive.json'
import themeTokens from './token/theme.json'
import typographyTokens from './token/typography.json'

type AnyObject = Record<string, any>

function getTokenValue(path: string): any {
  const parts = path.split('.')
  // search order: primitive -> themeTokens -> typographyTokens
  const sources: AnyObject[] = [primitive, themeTokens, typographyTokens]
  for (const src of sources) {
    let cur: any = src
    let found = true
    for (const p of parts) {
      if (cur == null || !(p in cur)) {
        found = false
        break
      }
      cur = cur[p]
    }
    if (!found) continue
    if (cur && typeof cur === 'object' && '$value' in cur) return cur['$value']
    return cur
  }
  return undefined
}

function resolveRef(value: string): string {
  // resolve nested references up to a safe depth
  let current: any = value
  let depth = 0
  while (typeof current === 'string') {
    const m = current.match(/^\{(.+)\}$/)
    if (!m) return current
    const path = m[1]
    const val = getTokenValue(path)
    if (val === undefined) return current // keep original if not found
    current = val
    if (++depth > 10) break
  }
  return String(current)
}

function resolveColorNode(node: AnyObject | undefined): string | undefined {
  if (!node) return undefined
  if (typeof node === 'string') return resolveRef(node)
  // node like { "$type": "color", "$value": "{colors.primary.500}" } or token mapping object
  if ('$value' in node) return resolveRef(node['$value'])
  return undefined
}

function resolveDimension(node: AnyObject | string | undefined): string | number | undefined {
  if (!node) return undefined
  if (typeof node === 'string') return resolveRef(node)
  if ('$value' in node) return resolveRef(node['$value'])
  return undefined
}

function mapPalette(tokens: AnyObject): AnyObject {
  const palette: AnyObject = {}

  // Primary
  palette.primary = {
    main: resolveColorNode(tokens.primary?.main),
    lightest: resolveColorNode(tokens.primary?.lightest),
    lighter: resolveColorNode(tokens.primary?.lighter),
    hover: resolveColorNode(tokens.primary?.hover),
    active: resolveColorNode(tokens.primary?.active),
    selected: resolveColorNode(tokens.primary?.selected),
    disabled: resolveColorNode(tokens.primary?.disabled),
  }

  // Secondary
  palette.secondary = {
    main: resolveColorNode(tokens.secondary?.main),
    lightest: resolveColorNode(tokens.secondary?.lightest),
    hover: resolveColorNode(tokens.secondary?.hover),
    active: resolveColorNode(tokens.secondary?.active),
    selected: resolveColorNode(tokens.secondary?.selected),
    disabled: resolveColorNode(tokens.secondary?.disabled),
  }

  palette.border = {
    main: resolveColorNode(tokens.border?.main),
  }

  palette.gradient = {
    mainLight: resolveColorNode(tokens.gradient['main-light']),
    mainDark: resolveColorNode(tokens.gradient['main-dark']),
  }

  palette.success = {
    light: resolveColorNode(tokens.success?.light),
    main: resolveColorNode(tokens.success?.main),
    dark: resolveColorNode(tokens.success?.dark),
  }

  palette.info = {
    light: resolveColorNode(tokens.info?.light),
    main: resolveColorNode(tokens.info?.main),
    dark: resolveColorNode(tokens.info?.dark),
  }

  palette.warning = {
    light: resolveColorNode(tokens.warning?.light),
    main: resolveColorNode(tokens.warning?.main),
    dark: resolveColorNode(tokens.warning?.dark),
  }

  palette.error = {
    light: resolveColorNode(tokens.error?.light),
    main: resolveColorNode(tokens.error?.main),
    dark: resolveColorNode(tokens.error?.dark),
  }

  palette.neutral = {
    main: resolveColorNode(tokens.neutral?.main),
    light: resolveColorNode(tokens.neutral?.light),
    lighter: resolveColorNode(tokens.neutral?.lighter),
    lightest: resolveColorNode(tokens.neutral?.lightest),
  }

  // Common
  if (tokens.common) {
    palette.common = {
      white: resolveColorNode(tokens.common.white),
      black: resolveColorNode(tokens.common.black),
    }
  }

  // Text
  if (tokens.text) {
    palette.text = {
      main: resolveColorNode(tokens.text.main),
      mainLight: resolveColorNode(tokens.text['main-light']),
      inverse: resolveColorNode(tokens.text.inverse),
      disabled: resolveColorNode(tokens.text.disabled),
      placeholder: resolveColorNode(tokens.text.placeholder),
    }
  }

  // Background (fallback to neutral tokens if not provided)
  if (tokens.bg) {
    palette.background = {
      disabled: resolveColorNode(tokens.bg.disabled),
      'a-hover-inverse': resolveColorNode(tokens.bg['a-hover-inverse']) || resolveColorNode(tokens.bg?.disabled),
      'a-hover-main': resolveColorNode(tokens.bg['a-hover-main']) || resolveColorNode(tokens.bg?.disabled),
      page: resolveColorNode(tokens.bg?.page) || resolveColorNode(tokens.bg?.disabled),
    }
  }

  return palette
}

function mapTypography(tokens: AnyObject): AnyObject {
  const typography: AnyObject = {}

  // helper to build variant
  const buildVariant = (node: AnyObject | undefined) => {
    if (!node) return undefined
    const ff = node.fontFamily ? resolveRef(node.fontFamily['$value'] ?? node.fontFamily) : undefined
    const fs = node.fontSize ? resolveDimension(node.fontSize) : undefined
    const lh = node.lineHeight ? resolveDimension(node.lineHeight) : undefined
    const fwRaw = node.fontWeight ?? node.fontWeight?.['$value']
    const fw = fwRaw ? (typeof fwRaw === 'string' && /^\d+$/.test(fwRaw) ? Number(fwRaw) : fwRaw) : undefined
    return {
      ...(ff ? { fontFamily: ff } : {}),
      ...(fs ? { fontSize: fs } : {}),
      ...(lh ? { lineHeight: lh } : {}),
      ...(fw ? { fontWeight: fw } : {}),
    }
  }

  // map common groups -> MUI variants
  // titles
  if (tokens.title) {
    typography.h1 = buildVariant(tokens.title.h1)
    typography.h2 = buildVariant(tokens.title.h2)
    typography.h3 = buildVariant(tokens.title.h3)
  }

  // display / page-title could be mapped to h1 override
  if (tokens.display?.['page-title']) {
    typography.h1 = { ...(typography.h1 || {}), ...(buildVariant(tokens.display['page-title']) || {}) }
  }

  // body
  if (tokens.body) {
    typography.body1 = buildVariant(tokens.body.main)
    typography.body2 = buildVariant(tokens.body.small)
  }

  // caption
  if (tokens.caption?.main) typography.caption = buildVariant(tokens.caption.main)

  return typography
}

function buildSpacingScale(spacingTokens: AnyObject) {
  const scale: Record<string, string> = {}
  for (const key of Object.keys(spacingTokens || {})) {
    const v = spacingTokens[key]
    const value = typeof v === 'object' && '$value' in v ? v['$value'] : v
    scale[key] = resolveRef(value)
  }
  return scale
}

export function createMuiThemeFromTokens() {
  const options: ThemeOptions = {}

  const paletteTokens = themeTokens.colors || {}
  options.palette = mapPalette(paletteTokens)

  const tt = typographyTokens || {}
  options.typography = mapTypography(tt)

  // Export spacing scale (not wired to MUI spacing function)
  const spacingScale = buildSpacingScale(themeTokens.spacing || {})

  // derive a base unit (px) from spacing tokens: take the smallest positive px value
  const pxValues: number[] = Object.values(spacingScale)
    .map((v) => (typeof v === 'string' ? Number(String(v).replace(/px$/, '')) : NaN))
    .filter((n) => !isNaN(n) && n > 0)
  const baseUnit = pxValues.length ? Math.min(...pxValues) : 8

  const toPx = (input: any): number | string => {
    if (typeof input === 'number') return input * baseUnit
    if (typeof input === 'string') {
      // token key in spacingScale
      if (spacingScale[input]) {
        const v = String(spacingScale[input])
        return v.endsWith('px') ? Number(v.replace(/px$/, '')) : Number(v)
      }
      // explicit "8px" or numeric string
      if (/^\d+(\.\d+)?px$/.test(input)) return input.endsWith('px') ? Number(input.replace(/px$/, '')) : Number(input)
      const num = Number(input)
      if (!isNaN(num)) return num * baseUnit
    }
    return input
  }

  // spacing function compatible with MUI
  const spacingFn: any = (...args: any[]) => {
    if (args.length === 0) return `${baseUnit}px`
    const parts = args.flat().map((a) => {
      const px = toPx(a)
      return typeof px === 'number' ? `${px}px` : String(px)
    })
    return parts.join(' ')
  }

  // attach token values as properties on spacingFn for convenient access
  // ex: theme.spacing['spacing-M'] and theme.spacing.M
  Object.keys(spacingScale).forEach((key) => {
    const val = spacingScale[key]
    spacingFn[key] = val
    const shortKey = key.replace(/^spacing-/, '')
    if (shortKey && shortKey !== key) spacingFn[shortKey] = val
  })

  options.spacing = spacingFn
  // create theme
  const theme = createTheme(options)

  // Ensure token props are present on the final theme.spacing (attach after createTheme)
  Object.keys(spacingScale).forEach((key) => {
    const val = spacingScale[key]
    const shortKey = key.replace(/^spacing-/, '')
    ;(theme.spacing as any)[key] = val
    if (shortKey && shortKey !== key) (theme.spacing as any)[shortKey] = val
  })

  // build size scale from primitive tokens and attach to theme
  const buildSizeScale = (primitiveSize: Record<string, any>) => {
    const out: Record<string, string> = {}
    Object.keys(primitiveSize || {}).forEach((k) => {
      const v = primitiveSize[k]
      out[k] = typeof v === 'object' && '$value' in v ? String(v['$value']) : String(v)
    })
    return out
  }

  const sizeScale = buildSizeScale(primitive.size || {})

  // attach size tokens on theme.size
  ;(theme as any).size = (theme as any).size || {}
  Object.keys(sizeScale).forEach((key) => {
    const val = sizeScale[key]
    // keep original key (ex: "0", "1", "small")
    ;(theme as any).size[key] = val
    // if numeric key, add "s<key>" alias -> theme.size.s0
    if (/^\d+$/.test(key)) {
      ;(theme as any).size[`s${key}`] = val
    }
    // also add camelCase short key for keys like "size-XL" => "XL"
    const shortKey = key.replace(/^size-/, '')
    if (shortKey && shortKey !== key) (theme as any).size[shortKey] = val
  })

  // build radius scale from theme tokens and attach to theme.radius
  const buildRadiusScale = (radiusTokens: Record<string, any>) => {
    const out: Record<string, string> = {}
    Object.keys(radiusTokens || {}).forEach((k) => {
      const v = radiusTokens[k]
      const value = typeof v === 'object' && '$value' in v ? v['$value'] : v
      // resolve references like "{size.2}" to actual primitive value ("4px")
      out[k] = resolveRef(value)
    })
    return out
  }

  const radiusScale = buildRadiusScale(themeTokens.radius || {})

  // attach radius tokens on theme.radius
  ;(theme as any).radius = (theme as any).radius || {}
  Object.keys(radiusScale).forEach((key) => {
    const val = radiusScale[key]
    ;(theme as any).radius[key] = val
    // add short alias: "radius-S" => "S"
    const shortKey = key.replace(/^radius-/, '')
    if (shortKey && shortKey !== key) (theme as any).radius[shortKey] = val
  })

  // build typography token map from typography.json and attach to theme.typographyTokens
  const buildTypographyScale = (typographyTokensInput: Record<string, any>) => {
    const out: Record<string, any> = {}
    Object.keys(typographyTokensInput || {}).forEach((k) => {
      const node = typographyTokensInput[k]
      // tokens use { "$type": "typography", "$value": { ... } } shape in typography.json
      const valueNode = node && node.$value ? node.$value : node
      if (typeof valueNode === 'object') {
        const resolved: Record<string, any> = {}
        Object.keys(valueNode).forEach((prop) => {
          const v = valueNode[prop]
          // if string attempt to resolve primitive refs like "{font.main.fontFamily}" or "{typography.*}" -> try resolveRef (falls back to original)
          if (typeof v === 'string') {
            resolved[prop] = resolveRef(v)
          } else {
            resolved[prop] = v
          }
        })
        out[k] = resolved
      } else {
        out[k] = valueNode
      }
    })
    return out
  }

  const typographyScale = buildTypographyScale(typographyTokens || {})

  // attach typography tokens on theme.typographyTokens
  ;(theme as any).typography = (theme as any).typography || {}
  Object.keys(typographyScale).forEach((key) => {
    const val = typographyScale[key]
    ;(theme as any).typography[key] = val
    // add short alias: remove common prefixes like "body-" or "display-" if present
    const shortKey = key.replace(/^(body-|display-)/, '')
    if (shortKey && shortKey !== key) (theme as any).typography[shortKey] = val
  })

  return {
    theme,
    spacingScale,
    sizeScale,
    radiusScale,
  }
}

export default createMuiThemeFromTokens
